import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Link,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import Grid from './Grid';
import {
  LinkedIn,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const footerStyle = {
    bgcolor: 'background.paper', // Use theme's paper color (white)
    color: 'text.primary', // Use theme's primary text color (dark grey)
    borderTop: `1px solid #E0E0E0`, // Use subtle border color
    pt: { xs: 4, md: 6 },
    pb: { xs: 3, md: 4 },
    mt: 'auto' // Push footer to bottom
  };

  const linkStyle = {
    textDecoration: 'none',
    color: 'text.secondary', // Use secondary text color
    '&:hover': {
      color: 'text.primary', // Darken on hover
      textDecoration: 'underline',
      textDecorationColor: 'rgba(74, 74, 74, 0.4)', // Lighter underline from theme
    },
  };

  const iconButtonStyle = {
    color: 'text.secondary', // Use secondary text color
    border: `1px solid #E0E0E0`, // Subtle border
    transition: 'all 0.3s ease',
    '&:hover': {
      bgcolor: 'rgba(74, 74, 74, 0.04)', // Very subtle background hover from theme
      color: 'text.primary',
      borderColor: '#CCCCCC',
    }
  };

  const serviceLinks = [
    { key: 'individueleCoaching', path: '/diensten/individuele-coaching' },
    { key: 'workshops', path: '/diensten/workshops' },
    { key: 'lesgeven', path: '/diensten/lesgeven' },
    { key: 'zelfcoaching', path: '/diensten/zelfcoaching' },
  ];

  return (
    <Box component="footer" sx={footerStyle}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Logo & Social */}
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src="/logo_sterkaanhetwerk.jpg" // Keep your logo path
              alt={language === 'nl' ? "Sterk aan het Werk Logo" : "Sterk aan het Werk Logo"}
              sx={{ height: 60, width: 'auto', mb: 2 }}
            />
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
              <IconButton
                component="a"
                href="https://www.linkedin.com/company/sterkaanhetwerk/"
                target="_blank"
                rel="noopener noreferrer"
                sx={iconButtonStyle}
              >
                <LinkedIn />
              </IconButton>
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} md={2}>
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 500 }}>
              {language === 'nl' ? 'Navigatie' : 'Navigation'}
            </Typography>
            <Stack spacing={1}>
              {[ 'home', 'diensten', 'events', 'tips', 'over', 'contact'].map((page) => (
                <Link
                  key={page}
                  component={RouterLink}
                  to={page === 'home' ? '/' : `/${page}`}
                  sx={linkStyle}
                >
                  {t.navigation[page]}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Services Links */}
          <Grid item xs={6} md={3}>
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 500 }}>
              {t.navigation.diensten} {/* Use translation for Services title */}
            </Typography>
            <Stack spacing={1}>
              {serviceLinks.map((service) => (
                <Link
                  key={service.key}
                  component={RouterLink}
                  to={service.path}
                  sx={linkStyle}
                >
                  {t.services[service.key]}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 500 }}>
              {t.navigation.contact} {/* Use translation for Contact title */}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              Hanneke van Keulen
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
              sterk-aanhetwerk@outlook.com
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t.common.kvk}: 90066200
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t.common.btw}: NL004785209B28
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: '#E0E0E0' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Sterk aan het Werk. Alle rechten voorbehouden.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 